import { useCallback, useSyncExternalStore } from "react";
import { shouldReduceMotion } from "../utils/performance";

function subscribe(callback) {
  const queries = [
    window.matchMedia("(max-width: 1023px)"),
    window.matchMedia("(hover: none) and (pointer: coarse)"),
    window.matchMedia("(prefers-reduced-motion: reduce)"),
  ];
  queries.forEach((mq) => mq.addEventListener("change", callback));
  return () => queries.forEach((mq) => mq.removeEventListener("change", callback));
}

function getSnapshot() {
  return shouldReduceMotion();
}

function getServerSnapshot() {
  return true;
}

const HIDDEN = {
  fadeUp: { opacity: 0, y: 16 },
  fadeDown: { opacity: 0, y: -16 },
  fadeLeft: { opacity: 0, x: -20 },
  fadeRight: { opacity: 0, x: 20 },
  fade: { opacity: 0, y: 30 },
  scale: { opacity: 0, scale: 0.95 },
};

const VISIBLE = { opacity: 1, y: 0, x: 0, scale: 1 };

/**
 * Mobile / touch: content visible immediately (no whileInView wait).
 * Desktop: unchanged scroll-reveal behaviour.
 */
export default function useAppMotion() {
  const isStatic = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const getProps = useCallback(
    ({ variant = "fadeUp", delay = 0, duration = 0.5 } = {}) => {
      if (isStatic) {
        return { initial: false, animate: VISIBLE };
      }
      const hidden = HIDDEN[variant] ?? HIDDEN.fadeUp;
      return {
        initial: hidden,
        whileInView: VISIBLE,
        viewport: { once: true, amount: 0.06 },
        transition: { duration, delay, ease: "easeOut" },
      };
    },
    [isStatic],
  );

  const getInViewVariants = useCallback(
    (variants) => {
      if (isStatic) {
        return { initial: false, animate: "visible", variants };
      }
      return {
        initial: "hidden",
        whileInView: "visible",
        viewport: { once: true, amount: 0.06 },
        variants,
      };
    },
    [isStatic],
  );

  return { isStatic, getProps, getInViewVariants };
}
